import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { Calendar, ChevronLeft, ChevronRight } from "react-feather";
import { RequiredSign } from "../../Constant/common";

/* ──────────────────────────────────────────────────────────────────
   Sub-components declared at module level (NOT during render)
   to comply with react-hooks/static-components rule
   ────────────────────────────────────────────────────────────────── */

const HeaderDisplay = React.forwardRef(
  (
    {
      onClick,
      $selectedDate,
      $dateValueFormat,
      $dayValueFormat,
      $dayColor,
      $accentColor,
      $disabled,
      $headerDisplay,
    },
    ref,
  ) => {
    const d = $selectedDate;
    return (
      <div
        onClick={$disabled ? undefined : onClick}
        ref={ref}
        className="bdp-header-display"
        style={{
          cursor: $disabled ? "not-allowed" : "pointer",
          textAlign: "center",
          userSelect: "none",
          opacity: $disabled ? 0.5 : 1,
        }}
      >
        {$headerDisplay ? (
          $headerDisplay
        ) : (
          <>
            <div
              className="bdp-header-date"
              style={{
                fontSize: "1.125rem",
                fontWeight: 800,
                color: "var(--text-primary)",
                letterSpacing: "0.01em",
              }}
            >
              {d ? moment(d).format($dateValueFormat) : "--"}
            </div>
            <div
              className="bdp-header-day"
              style={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: $dayColor || $accentColor || "var(--brand-indigo)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginTop: 2,
              }}
            >
              {d ? moment(d).format($dayValueFormat) : ""}
            </div>
          </>
        )}
      </div>
    );
  },
);
HeaderDisplay.displayName = "BaseDatePickerHeaderDisplay";

const DefaultInput = React.forwardRef(
  (
    {
      value: val,
      onClick,
      onChange: onChangeInput,
      onBlur: onBlurInput,
      placeholder: pl,
      id,
      disabled: dis,
      $showIcon,
      $iconPosition,
      $isInvalid,
    },
    ref,
  ) => (
    <div
      className={`bdp-input-wrapper ${$isInvalid ? "is-invalid" : ""} ${
        $iconPosition === "left" ? "bdp-icon-left" : "bdp-icon-right"
      } ${dis ? "is-disabled" : ""}`}
      onClick={() => !dis && ref && ref.current && ref.current.focus()}
    >
      {$showIcon && (
        <Calendar
          size={16}
          strokeWidth={2}
          className="bdp-input-icon"
          style={{
            color: $isInvalid ? "var(--danger)" : "var(--text-muted)",
          }}
        />
      )}
      <input
        id={id}
        ref={ref}
        type="text"
        readOnly
        value={val || ""}
        onClick={onClick}
        onChange={onChangeInput}
        onBlur={onBlurInput}
        placeholder={pl}
        disabled={dis}
        autoComplete="off"
        className={`form-control bdp-form-control ${$isInvalid ? "is-invalid" : ""} ${
          $showIcon ? ($iconPosition === "left" ? "ps-9" : "pe-9") : ""
        }`}
        style={{
          background: "var(--input-bg)",
          borderColor: $isInvalid ? "var(--danger)" : "var(--input-border)",
          color: "var(--text-primary)",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "var(--input-border-focus)";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)";
        }}
        onBlurCapture={(e) => {
          e.currentTarget.style.borderColor = $isInvalid ? "var(--danger)" : "var(--input-border)";
          e.currentTarget.style.boxShadow = "none";
          onBlurInput && onBlurInput(e);
        }}
      />
    </div>
  ),
);
DefaultInput.displayName = "BaseDatePickerDefaultInput";

/**
 * BaseDatePicker — project-wide consistent date picker.
 *
 * Props:
 *   variant       "default" | "header"  — form input style vs clickable date header display
 *   name          string                — field name (used for id + label htmlFor)
 *   label         node                  — label text / JSX
 *   value         string                — ISO date string "YYYY-MM-DD" (or null/undefined)
 *   selectedDate  Date                  — Alternative: pass Date object directly (for header mode)
 *   onChange      fn(val)               — called with "YYYY-MM-DD" (or "YYYY-MM") string (or "" when cleared)
 *   onDateChange  fn(date, str)         — called with Date object + formatted string (both)
 *   onBlur        fn                    — standard blur handler
 *   error         string                — validation error message
 *   touched       bool                  — whether field has been touched
 *   placeholder   string                — input placeholder
 *   required      bool                  — shows red * after label
 *   disabled      bool
 *   minDate       Date
 *   maxDate       Date
 *   showMonthYearPicker bool            — month+year picker mode
 *   showYearPicker  bool                — year-only picker mode
 *   dateFormat    string                — display format (defaults: "dd-MM-yyyy" / "MM/yyyy" / "yyyy")
 *   className     string                — extra class on wrapper div
 *   inputClassName string               — extra class on input element
 *   showIcon      bool                  — show calendar icon inside input (default variant)
 *   iconPosition  "left" | "right"      — icon position (default: right)
 *   portalId      string                — portal element ID (default: "root")
 *   popperPlacement string              — popper.js placement (default: "bottom-start")
 *   withPortal    bool                  — render calendar in portal (default: false)
 *
 *   HEADER VARIANT ONLY:
 *   headerDisplay    node              — Custom JSX for the clickable display
 *   dateValueFormat  string            — Format for the big date line (header variant)
 *   dayValueFormat   string            — Format for the day line (header variant)
 *   accentColor      string            — CSS color for the day text accent (header variant)
 *   dayColor         string            — CSS color for day text (defaults to --brand-indigo)
 */
const BaseDatePicker = ({
  variant = "default",
  name,
  label,
  value,
  selectedDate: selectedDateProp,
  onChange,
  onDateChange,
  onBlur,
  error,
  touched,
  placeholder,
  required,
  disabled,
  minDate,
  maxDate,
  showMonthYearPicker = false,
  showYearPicker = false,
  dateFormat,
  className,
  inputClassName,
  showIcon = true,
  iconPosition = "right",
  portalId = "root",
  popperPlacement = "bottom-start",
  withPortal = false,

  dateValueFormat = "DD-MM-YYYY",
  dayValueFormat = "dddd",
  accentColor,
  dayColor,
  headerDisplay,
}) => {
  const isInvalid = !!(touched && error);

  /* Decide which Date object to feed react-datepicker */
  const selectedDate = React.useMemo(() => {
    if (selectedDateProp instanceof Date) return selectedDateProp;
    if (!value) return null;
    let fmt = "YYYY-MM-DD";
    if (showMonthYearPicker) fmt = "YYYY-MM";
    if (showYearPicker) fmt = "YYYY";
    const m = moment(value, fmt, true);
    return m.isValid() ? m.toDate() : null;
  }, [value, selectedDateProp, showMonthYearPicker, showYearPicker]);

  /* Storage format for onChange string */
  const storageFormat = React.useMemo(() => {
    if (showMonthYearPicker) return "YYYY-MM";
    if (showYearPicker) return "YYYY";
    return "YYYY-MM-DD";
  }, [showMonthYearPicker, showYearPicker]);

  /* Display format in input */
  const displayFormat = React.useMemo(() => {
    if (dateFormat) return dateFormat;
    if (showYearPicker) return "yyyy";
    if (showMonthYearPicker) return "MM/yyyy";
    return "dd-MM-yyyy";
  }, [dateFormat, showMonthYearPicker, showYearPicker]);

  const handleChange = (date) => {
    if (!date) {
      onChange && onChange("");
      onDateChange && onDateChange(null, "");
      return;
    }
    const formatted = moment(date).format(storageFormat);
    onChange && onChange(formatted);
    onDateChange && onDateChange(date, formatted);
  };

  const customHeaderRenderer = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    prevYearButtonDisabled,
    nextYearButtonDisabled,
  }) => {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const startY = minDate ? minDate.getFullYear() : new Date().getFullYear() - 15;
    const endY = maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 15;
    const yearsList = [];
    for (let y = startY; y <= endY; y++) {
      yearsList.push(y);
    }
    const monthsList = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div
        className="bdp-custom-header d-flex align-items-center justify-content-between px-1"
        style={{ gap: "8px" }}
      >
        <button
          type="button"
          onClick={showMonthYearPicker ? decreaseYear : decreaseMonth}
          disabled={showMonthYearPicker ? prevYearButtonDisabled : prevMonthButtonDisabled}
          className="bdp-nav-btn btn btn-sm p-0 d-flex align-items-center justify-content-center"
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            border: "1px solid var(--glass-border)",
            background: "var(--icon-btn-bg, transparent)",
            color: "var(--text-primary)",
          }}
        >
          <ChevronLeft size={16} />
        </button>

        <div className="d-flex align-items-center justify-content-center" style={{ flex: 1 }}>
          {!showMonthYearPicker && !showYearPicker && (
            <select
              value={monthsList[currentMonth]}
              onChange={({ target: { value } }) => changeMonth(monthsList.indexOf(value))}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="bdp-header-select-label"
            >
              {monthsList.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          )}

          {!showYearPicker && (
            <select
              value={currentYear}
              onChange={({ target: { value } }) => changeYear(Number(value))}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="bdp-header-select-label"
            >
              {yearsList.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          )}
        </div>

        <button
          type="button"
          onClick={showMonthYearPicker ? increaseYear : increaseMonth}
          disabled={showMonthYearPicker ? nextYearButtonDisabled : nextMonthButtonDisabled}
          className="bdp-nav-btn btn btn-sm p-0 d-flex align-items-center justify-content-center"
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            border: "1px solid var(--glass-border)",
            background: "var(--icon-btn-bg, transparent)",
            color: "var(--text-primary)",
          }}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  /* ─────────────── Variant: HEADER (clickable centered date display) ─── */
  if (variant === "header") {
    return (
      <div className={`bdp-wrapper bdp-wrapper--header ${className || ""}`}>
        <DatePicker
          id={name}
          name={name}
          selected={selectedDate}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          showMonthYearPicker={showMonthYearPicker}
          showYearPicker={showYearPicker}
          dateFormat={displayFormat}
          placeholderText={placeholder}
          customInput={
            <HeaderDisplay
              $selectedDate={selectedDate}
              $dateValueFormat={dateValueFormat}
              $dayValueFormat={dayValueFormat}
              $dayColor={dayColor}
              $accentColor={accentColor}
              $disabled={disabled}
              $headerDisplay={headerDisplay}
            />
          }
          withPortal={withPortal}
          popperPlacement={popperPlacement}
          portalId={portalId}
          renderCustomHeader={showYearPicker ? undefined : customHeaderRenderer}
          popperModifiers={[
            {
              name: "flip",
              enabled: true,
              options: {
                padding: 8,
              },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                padding: 8,
              },
            },
          ]}
          autoComplete="off"
          className="bdp-header-picker"
        />
      </div>
    );
  }

  /* ─────────────── Variant: DEFAULT (Bootstrap form input) ─── */
  return (
    <div className={`mb-3 bdp-wrapper bdp-wrapper--default ${className || ""}`}>
      {label && (
        <label
          htmlFor={name}
          className="form-label bdp-label"
          style={{ color: "var(--text-primary)" }}
        >
          {label}
          {required && <span className="text-danger ms-1">{RequiredSign}</span>}
        </label>
      )}
      <DatePicker
        id={name}
        name={name}
        selected={selectedDate}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        showMonthYearPicker={showMonthYearPicker}
        showYearPicker={showYearPicker}
        dateFormat={displayFormat}
        placeholderText={
          placeholder ||
          (showYearPicker ? "Select year" : showMonthYearPicker ? "Select month" : "Select date")
        }
        customInput={
          <DefaultInput
            $showIcon={showIcon}
            $iconPosition={iconPosition}
            $isInvalid={isInvalid}
            inputClassName={inputClassName}
          />
        }
        withPortal={withPortal}
        popperPlacement={popperPlacement}
        portalId={portalId}
        renderCustomHeader={showYearPicker ? undefined : customHeaderRenderer}
        popperModifiers={[
          {
            name: "flip",
            enabled: true,
            options: {
              padding: 8,
            },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              padding: 8,
            },
          },
        ]}
        autoComplete="off"
      />
      {isInvalid && <div className="invalid-feedback d-block bdp-error">{error}</div>}
    </div>
  );
};

export default BaseDatePicker;
